import { Grid, MenuItem, TextField } from "@material-ui/core";
import { useParams } from "react-router-dom";
import { useQuery } from "../../../../hooks/useQuery";
import { planEatApi } from "../../../../planEatApi/PlanEatApi";
import { IOfficeContent } from "../../../../planEatApi/PlanEatFacade";
import { AdminPicker } from "../../components/AdminPicker";
import { EntityDetailLayout, Section } from "../../components/EntityDetailLayout";
import { EntityEditorConfig, useEntityEditor } from "../../components/useEntityEditor";
import { hierarchyCrumbs } from "../../components/VersionsPage";
import { useSysadminData } from "../../SysadminData";
import { sysadminPaths } from "../../sysadminUtils";
import { EaterGroupsTable } from "../EaterGroup/EaterGroups";

const officeConfig: EntityEditorConfig<IOfficeContent> = {
    label: "Office",
    section: "offices",
    empty: {
        id: "",
        name: "",
        description: "",
        companyId: "",
        officeName: "",
        officeAddress: "",
        officeAdminIds: [],
        defaultSeatings: [],
        defaultAvailability: [],
        defaultDishesContent: [],
        officeSettings: undefined,
    },
    get: planEatApi.getOffice,
    getVersion: planEatApi.getVersionForOffice,
    post: planEatApi.postOffice,
    remove: planEatApi.postDeleteForOffice,
};

export const Office: React.FC = () => {
    const { officeId, version } = useParams<{ officeId?: string; version?: string }>();
    const query = useQuery();
    const data = useSysadminData();
    const editor = useEntityEditor(officeConfig, officeId, version, { companyId: query.get("companyId") ?? "" });
    const { entity: office, setField, isVersion } = editor;

    const missing: string[] = [];
    if (!office.officeName.trim()) missing.push("name");
    if (!office.companyId) missing.push("company");
    if ((office.officeAdminIds ?? []).length === 0) missing.push("at least one admin");

    const eaterGroupCount = data.eaterGroups.filter(g => g.content.officeId === office.id).length;
    const title = editor.isNew ? "New office" : office.officeName || "Unnamed office";
    const companies = [...data.companies].sort((a, b) => a.content.companyName.localeCompare(b.content.companyName));

    return (
        <EntityDetailLayout
            label="Office"
            section="offices"
            title={title}
            breadcrumbs={[...hierarchyCrumbs(data, "offices", { companyId: office.companyId }), { label: title }]}
            editor={editor}
            missing={missing}
            parentPath={office.companyId && data.companyById(office.companyId) ? sysadminPaths.detail("companies", office.companyId) : sysadminPaths.list("offices")}
            deleteWarning={eaterGroupCount > 0 ? <p>The office still has <b>{eaterGroupCount} eater group(s)</b>. They are not deleted automatically.</p> : undefined}
            related={<EaterGroupsTable officeId={office.id} title="Eater groups" />}
        >
            <Section title="Details">
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth required variant="outlined" label="Name" value={office.officeName} disabled={isVersion}
                            onChange={e => setField("officeName", e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth required select variant="outlined" label="Company" value={office.companyId} disabled={isVersion}
                            onChange={e => setField("companyId", e.target.value)}
                            helperText={office.companyId && !data.companyById(office.companyId) && !data.loading ? "The company no longer exists" : undefined}>
                            {companies.map(c => <MenuItem key={c.content.id} value={c.content.id}>{c.content.companyName}</MenuItem>)}
                            {office.companyId && !data.companyById(office.companyId) && <MenuItem value={office.companyId} disabled>Unknown company</MenuItem>}
                        </TextField>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth variant="outlined" label="Address" value={office.officeAddress} disabled={isVersion}
                            onChange={e => setField("officeAddress", e.target.value)} />
                    </Grid>
                </Grid>
            </Section>
            <Section title="Admins" description="Admins are given the office_manager_users role.">
                <AdminPicker value={office.officeAdminIds ?? []} readOnly={isVersion} onChange={ids => setField("officeAdminIds", ids)} />
            </Section>
        </EntityDetailLayout>
    );
};
